import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../api/client';

interface User {
  id: number;
  google_id: string;
  email: string;
  name: string;
  avatar: string;
  is_paid: number;
  subscription_plan: string | null;
  subscription_end: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  login: (token: string) => void;
  logout: () => void;
  fetchUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('jwt_token'),
  );
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user_data');
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(false);

  const isAdmin = Boolean(
    user?.email && user.email === import.meta.env.VITE_ADMIN_EMAIL,
  );

  function login(newToken: string) {
    localStorage.setItem('jwt_token', newToken);
    setToken(newToken);
  }

  function logout() {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('user_data');
    setToken(null);
    setUser(null);
  }

  async function fetchUser() {
    if (!token) return;
    setLoading(true);
    try {
      const res = await apiClient.get('/user/me');
      const userData = res.data.data;
      setUser(userData);
      localStorage.setItem('user_data', JSON.stringify(userData));
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (token && !user) {
      fetchUser();
    }
  }, [token]);

  return (
    <AuthContext.Provider
      value={{ token, user, isAdmin, loading, login, logout, fetchUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
